import type {PropsWithChildren} from 'react';
import React, {useMemo} from 'react';
import type {TypographyTypeMap} from '@mui/material/Typography';
import Typography from '@mui/material/Typography';
import type {TextProps} from './types';

export const Text: React.FC<PropsWithChildren<TextProps>> = ({
  variant,
  kind,
  mode,
  sx,
  component = 'p',
  textTransform,
  keepWhitespace,
  children,
  className,
  noWrap,
  onClick,
  'data-sentry-mask': dataSentryMask,
}) => {
  const resolvedSx = useMemo(
    (): TypographyTypeMap['props']['sx'] =>
      textTransform || keepWhitespace
        ? {
            ...sx,
            whiteSpace: keepWhitespace ? 'pre-wrap' : undefined,
            textTransform,
          }
        : sx,
    [keepWhitespace, sx, textTransform],
  );

  // Only resolve a palette color when kind and/or mode is set; otherwise leave
  // the color to be inherited (and don't clobber any color passed via `sx`).
  // The resolved value is a MUI palette path (e.g. 'primary.main') that the sx
  // system maps to the corresponding theme palette color.
  const color =
    kind !== undefined || mode !== undefined
      ? `${kind ?? 'primary'}.${mode ?? 'main'}`
      : undefined;

  return (
    <Typography
      data-sentry-mask={dataSentryMask}
      className={className}
      variant={variant}
      component={component}
      noWrap={noWrap}
      onClick={onClick}
      sx={color !== undefined ? {...resolvedSx, color} : resolvedSx}>
      {children}
    </Typography>
  );
};
